import {
  useCallback,
  useEffect,
  useRef,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';

interface DismissablePopoverOptions {
  open: boolean;
  /** Close the popover AND reset any transient state (active option, etc.). */
  onClose: () => void;
  /**
   * Focused when Escape closes the popover — the combobox input or the toggle
   * button. Deliberately NOT focused on outside-click/focus-out: the user is
   * already moving focus somewhere else and stealing it back is hostile.
   */
  focusOnClose?: RefObject<HTMLElement | null>;
  /**
   * Panels rendered outside `rootRef` (portals) that still count as "inside"
   * for dismissal. React events bubble through the React tree, so the root's
   * blur/keydown handlers already see portalled children; only the DOM-level
   * outside-click check needs to be told about them.
   */
  extraRefs?: Array<RefObject<HTMLElement | null>>;
}

const NO_EXTRA_REFS: Array<RefObject<HTMLElement | null>> = [];

/**
 * The one dismissal contract every popover/combobox in this app uses:
 *
 * 1. Escape closes it and returns focus to the trigger (input or toggle).
 * 2. A pointer press anywhere outside closes it.
 * 3. Focus leaving the popover (Tab, or clicking another field) closes it.
 *
 * Options/panel contents must additionally be unrendered — or `tabIndex={-1}` —
 * while closed so they never sit in the tab order. That part belongs to the
 * component, since only it knows its own markup.
 *
 * Escape is handled as a React keydown on the root rather than a document
 * listener on purpose: a document/window listener would fight `useModalA11y`,
 * which already claims Escape at window-capture for dialogs.
 */
export function useDismissablePopover<T extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  focusOnClose,
  extraRefs = NO_EXTRA_REFS,
}: DismissablePopoverOptions) {
  const rootRef = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const extraRefsRef = useRef(extraRefs);
  extraRefsRef.current = extraRefs;

  /** True when `node` lives inside the popover root or one of its portals. */
  const containsTarget = useCallback((node: Node | null | undefined) => {
    if (!node) return false;
    if (rootRef.current?.contains(node)) return true;
    return extraRefsRef.current.some((ref) => !!ref.current?.contains(node));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event: MouseEvent) => {
      if (!containsTarget(event.target as Node)) onCloseRef.current();
    };
    // mousedown, not click: the popover must be gone before the click lands,
    // so a click aimed at a control behind it is not swallowed by a re-render.
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [open, containsTarget]);

  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !open) return;
    event.preventDefault();
    // Stop the key from also closing an enclosing drawer/modal — closing the
    // popover is the whole intent of the first Escape.
    event.stopPropagation();
    onCloseRef.current();
    focusOnClose?.current?.focus();
  }, [open, focusOnClose]);

  const handleFocusOut = useCallback((event: FocusEvent) => {
    if (event.relatedTarget instanceof Node && containsTarget(event.relatedTarget)) return;
    onCloseRef.current();
  }, [containsTarget]);

  return { rootRef, containsTarget, handleEscapeKey, handleFocusOut };
}
